import { useView } from 'aurelia-framework';
import { autoinject, transient } from "aurelia-dependency-injection";
import {VesselComparisonVesselOptionVm} from "../view-models/vessel-comparison-vessel-option-vm";
import {VesselComparisonSocialVm} from "../view-models/vessel-comparison-social-vm";
import {VesselComparisonEnvironmentVm} from "../view-models/vessel-comparison-environment-vm";
import {VesselComparisionGovernanceVm} from "../view-models/vessel-comparision-governance-vm";
import {VesselComparisonService} from "../../../../../services/vessel-comparison/vessel-comparison.service";
import {EntityTypeConfigurationHelper} from "../../../../../models/basics/basics.models";
import { Claims } from "../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { computedFrom } from "aurelia-binding";
import { VesselComparisonGovernanceDetails } from "../../../../../models/vessel-comparison/vessel-comparison.models";

@autoinject
@transient()
@useView('views/pages/modules/vessel-comparison/tabs/esg-indicators.html')
export class EsgIndicators {
    private _canAddAnotherVessel: boolean;
    refPageEsgIndicators: HTMLElement;
    isSubScoresShowing: boolean;
    vesselOptionsVms: VesselComparisonVesselOptionVm[] =  [];
    vesselComparisonSocialVms: VesselComparisonSocialVm[] = [];
    vesselComparisonEnvironmentVms: VesselComparisonEnvironmentVm[] = [];
    vesselComparisionGovernanceVms: VesselComparisionGovernanceVm[] = [];

    public showGHG: boolean;
    public showCrewWelfareInVessel: boolean = false;

    @computedFrom("_canAddAnotherVessel")
    get canAddAnotherVessel(): boolean {
        return this._canAddAnotherVessel;
    }

    constructor(private vesselComparisonService: VesselComparisonService, 
                private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, userClaimsService: UserClaimsService) { 
                    
        this.showGHG = userClaimsService.hasPermission(Claims.ShowGHG);
        this.showCrewWelfareInVessel = userClaimsService.hasPermission(Claims.ShowCrewWelfareInVessel);
    }
    
    activate(model) {
        this.addAnotherVessel();
        if(model && model.vesselId) {
            this.vesselOptionsVms[0].searchVesselSelected(null, 0, model.vesselId);
        }
    }

    addAnotherVessel() : void {
        this.vesselOptionsVms.push(new VesselComparisonVesselOptionVm({
            vesselId: null,
            vesselName: null,
            imo: null,
            dwt: null,
            age: null,
            constructionYear: null,
            flag: null,
            vesselType: null,
            vesselComparisonEnvironmentDetails: null,
            vesselComparisonSocialDetails: null,
            vesselComparisonGovernanceDetails: null,
            isSanctioned: null,
            isFlagFake: null
        }, this.entityTypeConfigurationHelper, (vesselId: number, index: number) => this.reloadVesselDetails(vesselId, index)));
        this.updateCanAddAnotherVessel()
    }
    
    reloadVesselDetails (vesselId: number, index: number) : void {
        if(this.vesselOptionsVms.length < 4 || this.vesselOptionsVms[index]) {
            this.vesselComparisonService.getVesselDetails({vesselId: vesselId}).then(response => {
                this.vesselOptionsVms[index].selectedVessel = response.result;

                if (this.vesselComparisonEnvironmentVms[index]) {
                    this.vesselComparisonEnvironmentVms[index].vesselComparisonEnvironmentDetails = response.result.vesselComparisonEnvironmentDetails;
                    this.vesselComparisonEnvironmentVms[index].isSanctioned = response.result.isSanctioned;
                    this.vesselComparisonEnvironmentVms[index].isFlagFake = response.result.isFlagFake;
                }
                else {
                    this.vesselComparisonEnvironmentVms.push(new VesselComparisonEnvironmentVm(response.result.vesselComparisonEnvironmentDetails, response.result.vesselComparisonGovernanceDetails.starRating, response.result.isSanctioned, response.result.isFlagFake));
                }
                
                if (this.vesselComparisonSocialVms[index]) {
                    this.vesselComparisonSocialVms[index].vesselSocialVesselDetails = response.result.vesselComparisonSocialDetails;
                    this.vesselComparisonSocialVms[index].isSanctioned = response.result.isSanctioned;
                    this.vesselComparisonSocialVms[index].isFlagFake = response.result.isFlagFake;
                }
                else {
                    this.vesselComparisonSocialVms.push(new VesselComparisonSocialVm(response.result.vesselComparisonSocialDetails, response.result.isSanctioned, response.result.isFlagFake));
                }

                if (this.vesselComparisionGovernanceVms[index]) {
                    this.vesselComparisionGovernanceVms[index].vesselComparisonGovernanceDetails = response.result.vesselComparisonGovernanceDetails;
                    this.vesselComparisionGovernanceVms[index].isSanctioned = response.result.isSanctioned;
                    this.vesselComparisionGovernanceVms[index].isFlagFake = response.result.isFlagFake;
                }
                else {
                    this.vesselComparisionGovernanceVms.push(new VesselComparisionGovernanceVm(this.arrangeCertificates(response.result.vesselComparisonGovernanceDetails), response.result.isSanctioned, response.result.isFlagFake));
                }
                this.updateCanAddAnotherVessel();
            }, error => {
                console.log(error)
            });
        }
    }

    //Rightship member certificates
    arrangeCertificates (governanceVesselDetails: VesselComparisonGovernanceDetails): VesselComparisonGovernanceDetails {
        const rightshipMemberCertificateUIDs = [
            'RightshipMembersDOC',
            'RightshipMembersCM',
            'RightshipMembersTM',
            'RightshipMembersCO',
            'RightshipMembersBO',
            'RightshipMembersRO'
        ];
        
        //Get only rightship member certificates
        const rightshipMembersCertificates = governanceVesselDetails.vesselCertificates.filter(x => rightshipMemberCertificateUIDs.includes(x.certificateUID));
        //Get remaining certificates
        const allCertificates = governanceVesselDetails.vesselCertificates.filter(x => !rightshipMembersCertificates.includes(x));

        if (rightshipMembersCertificates.length > 0) {
            const rightshipMembersCertificate = rightshipMembersCertificates[0];
            
            //We will empty the description for the RS member certificates to reset it before generating a new one
            rightshipMembersCertificate.vesselDescription = '';

            //Text is different when we have 1 RS member certificate than when we have more than 1
            //Generate text for when we have 1 RS member certificate
            if (rightshipMembersCertificates.length == 1){
                rightshipMembersCertificate.vesselDescription = 'The ' + rightshipMembersCertificate.certificateUID.replace('RightshipMembers', '') + ' company of this vessel is a member of RightShip';
            }
            else { //Generate text for multiple RS member certificates
                rightshipMembersCertificates.forEach(certificate => {      
                    if (!rightshipMembersCertificate.vesselDescription){
                        rightshipMembersCertificate.vesselDescription = 'The ';
                    }
                    else {
                        rightshipMembersCertificate.vesselDescription += ', ';
                    }                
    
                    rightshipMembersCertificate.vesselDescription += `${certificate.certificateUID.replace('RightshipMembers', '')} company`;
                });
                rightshipMembersCertificate.vesselDescription += ' of this vessel are members of RightShip';
            }

            //Pushing the newly created certificate to the array of the other certificates
            allCertificates.push(rightshipMembersCertificate);
        }

        governanceVesselDetails.vesselCertificates = allCertificates;
        return governanceVesselDetails;
    }

    removeVessel (index: number) : void {
        if (index != null && index >= 0) {
            this.vesselOptionsVms.splice(index, 1);
            this.vesselComparisonEnvironmentVms.splice(index, 1);
            this.vesselComparisonSocialVms.splice(index, 1);
            this.vesselComparisionGovernanceVms.splice(index, 1);
            this.updateCanAddAnotherVessel();
        }
    }

    updateCanAddAnotherVessel() {
        if(!this.vesselOptionsVms || this.vesselOptionsVms.length == 0) {
            this._canAddAnotherVessel = true;
            return;
        }

        const lastIndex = this.vesselOptionsVms.length - 1;
        if (!this.vesselOptionsVms[lastIndex] || !this.vesselOptionsVms[lastIndex].selectedVessel.vesselId) {
            this._canAddAnotherVessel = false;
        }
        else {
            this._canAddAnotherVessel = true;
        }
    }

    @computedFrom('vesselOptionsVms', 'vesselOptionsVms.length')
    get canAddMoreVessels() : boolean {
        return this.vesselOptionsVms.length < 4;
    }

    @computedFrom('vesselOptionsVms', 'vesselOptionsVms.length', 'vesselOptionsVms[0].selectedVessel')
    get enableGenerateReport(): boolean {
        return !(this.vesselOptionsVms.length == 0 ||
            (this.vesselOptionsVms.length == 1 && this.vesselOptionsVms[0].selectedVessel && !this.vesselOptionsVms[0].selectedVessel.vesselId)) 
    }

    @computedFrom('refPageEsgIndicators', 'refPageEsgIndicators.scrollWidth')
    get pageScrollWidth() {
        if(this.refPageEsgIndicators) {
            return this.refPageEsgIndicators.scrollWidth - 20;
        }
    }

    toggleSubScores() {
        this.isSubScoresShowing = !this.isSubScoresShowing;
    }

    detached() {
        this.vesselOptionsVms =  [];
        this.vesselComparisonSocialVms = [];
        this.vesselComparisonEnvironmentVms = [];
        this.vesselComparisionGovernanceVms = [];
    }
}
